import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { core } from "@/lib/mh";
import { errText, copyText } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Panel } from "@/components/mh/bits";
import { Btn, Copyable } from "@/components/mh/controls";
import { UsageReportView } from "@/components/mh/UsageReportView";
import { PublicTop } from "@/pages/Audit";

const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });

const ShareBox = ({ share, month, onChange, canEdit }) => {
  const url = share ? `${window.location.origin}/usage/${share.token}` : "";
  const create = async () => { try { onChange((await core.createShare(month)).data); toast.success("Share link is on"); } catch (e) { toast.error(errText(e)); } };
  const toggle = async (v) => { try { await core.toggleShare(share.token, v); onChange({ ...share, enabled: v }); toast.success(v ? "Link switched on" : "Link switched off"); } catch (e) { toast.error(errText(e)); } };
  return (
    <Panel cls="p-5 space-y-3" testId="usage-share">
      <Eyebrow>Share with the client</Eyebrow>
      {!share ? (
        canEdit ? <Btn variant="primary" onClick={create} testId="usage-share-create">Create share link</Btn> : <p className="text-sm text-muted-foreground">Only the owner can create a share link.</p>
      ) : (
        <>
          <label className="flex items-center gap-3 text-sm"><Switch checked={share.enabled} onCheckedChange={toggle} disabled={!canEdit} data-testid="usage-share-toggle" />{share.enabled ? "Anyone with the link can view" : "Link is off"}</label>
          {share.enabled && <Copyable label="Public link" value={url} testId="usage-share-link" onCopy={() => toast.success("Copied")} />}
        </>
      )}
    </Panel>
  );
};

export default function Reports() {
  const { isOwner, current } = useAuth();
  const [month, setMonth] = useState(null);
  const [r, setR] = useState(null);
  const load = useCallback(() => core.usageReport(month || undefined).then((x) => { setR(x.data); if (!month) setMonth(x.data.month); }).catch((e) => toast.error(errText(e))), [month]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div className="h-[50vh] grid place-items-center font-mono text-xs text-muted-foreground">Building report…</div>;
  return (
    <div className="max-w-[1200px] mx-auto space-y-6" data-testid="reports-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mh-rise">
        <div><Eyebrow>{current?.name} · usage report</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">{monthLabel(r.month)}, <em className="text-muted-foreground">in numbers.</em></h1></div>
        <div className="flex items-center gap-2">
          <Select value={month || r.month} onValueChange={setMonth}>
            <SelectTrigger className="h-10 w-48 rounded-full bg-white/[0.03] border-white/10" data-testid="usage-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0F] border-white/10">{r.months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <Btn onClick={() => { copyText(JSON.stringify(r, null, 2)); toast.success("Report data copied"); }} testId="usage-copy-json">Copy data</Btn>
        </div>
      </header>
      <ShareBox key={r.month} share={r.share} month={r.month} canEdit={isOwner} onChange={(s) => setR((x) => ({ ...x, share: s }))} />
      <UsageReportView r={r} />
    </div>
  );
}

export function SharedUsageReport() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { core.sharedReport(token).then((x) => setR(x.data)).catch((e) => setR({ error: errText(e) })); }, [token]);
  return (
    <div className="min-h-screen bg-[#030305]" data-testid="shared-usage-page">
      <PublicTop />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10 space-y-6">
        {!r && <p className="font-mono text-xs text-muted-foreground">Loading report…</p>}
        {r?.error && <p className="text-muted-foreground py-20 text-center" data-testid="shared-usage-error">{r.error}</p>}
        {r && !r.error && (
          <>
            <header><Eyebrow>Monthly usage report · generated {new Date(r.generated_at).toLocaleString()}</Eyebrow>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">{r.workspace.name}: <em className="text-muted-foreground">{monthLabel(r.month)}</em></h1></header>
            <UsageReportView r={r} />
          </>
        )}
      </main>
    </div>
  );
}
